import { createContext, useContext, useCallback, useMemo, useRef, useSyncExternalStore } from "react";

const TooltipContext = createContext(null);

export const TooltipProvider = ({ children }) => {
  const registryRef = useRef({});
  const listenersRef = useRef(new Set());

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const getSnapshot = useCallback(() => registryRef.current, []);

  const register = useCallback((id, content) => {
    registryRef.current = { ...registryRef.current, [id]: content };
    listenersRef.current.forEach((l) => l());
    return () => {
      const { [id]: _, ...rest } = registryRef.current;
      registryRef.current = rest;
      listenersRef.current.forEach((l) => l());
    };
  }, []);

  // Callbacks are useCallback([])-stable, so the value only needs building once.
  // Without this, any root re-render (toast/theme/route) mints a new value
  // object and re-renders every tooltip consumer via context.
  const value = useMemo(
    () => ({ register, subscribe, getSnapshot }),
    [register, subscribe, getSnapshot]
  );

  return (
    <TooltipContext.Provider value={value}>
      {children}
    </TooltipContext.Provider>
  );
};

export const useRegisterTooltip = (id) => {
  const ctx = useContext(TooltipContext);
  if (!ctx) return null;
  return { register: ctx.register, id };
};

export const useTooltipRegistry = () => {
  const ctx = useContext(TooltipContext);
  if (!ctx) return {};
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useSyncExternalStore(ctx.subscribe, ctx.getSnapshot);
};

export default TooltipContext;
